import * as Sentry from '@sentry/react';

import type { SeverityLevel } from '@sentry/react';

export type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error';

/** Sin datos de usuario: la identidad la pone InitsContainer solo con el guid (DEU-286). */
export type LogContext = {
  error?: unknown;
  extra?: Record<string, unknown>;
  tags?: Record<string, string | number | boolean>;
};

type LogMethod = (message: string, context?: LogContext) => void;

const SENTRY_LEVEL: Record<LogLevel, SeverityLevel> = {
  trace: 'debug',
  debug: 'debug',
  info: 'info',
  warn: 'warning',
  error: 'error',
};

const CONSOLE_METHOD: Record<LogLevel, 'debug' | 'info' | 'warn' | 'error'> = {
  trace: 'debug',
  debug: 'debug',
  info: 'info',
  warn: 'warn',
  error: 'error',
};

const MAX_KEYS_IN_MESSAGE = 10;

// De un objeto rechazado solo salen su nombre y su mensaje, o los nombres de sus claves, nunca los valores: puede ser
// una respuesta del API con datos del alumno (security.md, regla 4). Con String() llegaba como «[object Object]».
function describeRejection(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return value.toString();
  if (typeof value === 'object' && value !== null) {
    const { name, message } = value as { name?: unknown; message?: unknown };
    if (typeof message === 'string') return typeof name === 'string' ? `${name}: ${message}` : message;
    return `Rechazo con un objeto (claves: ${Object.keys(value).slice(0, MAX_KEYS_IN_MESSAGE).join(', ')})`;
  }
  return `Rechazo no serializable (${typeof value})`;
}

function hasError(error: unknown): boolean {
  return error !== undefined && error !== null;
}

function describeError(error: unknown): Record<string, string> {
  return error instanceof Error ? { errorName: error.name, errorMessage: error.message } : { rejection: describeRejection(error) };
}

// Solo en desarrollo: en los tests (NODE_ENV=test) un console.warn/error haría fallar al consoleGuard
// cualquier test que pase por un aviso.
function writeToConsole(level: LogLevel, message: string, context?: LogContext): void {
  if (process.env.NODE_ENV !== 'development') return;
  globalThis.console[CONSOLE_METHOD[level]](`[logger] ${message}`, context ?? {});
}

// `debug`/`info` no son eventos: viajan como miga con el siguiente error real y no gastan cuota. Con el SDK 9.41+
// pasarán a Sentry Logs (DEU-323) sin cambiar a quien los llama.
function leaveBreadcrumb(level: LogLevel, message: string, { error, extra, tags }: LogContext = {}): void {
  const errorData = hasError(error) ? describeError(error) : {};
  Sentry.addBreadcrumb({ message, category: 'log', level: SENTRY_LEVEL[level], data: { ...tags, ...extra, ...errorData } });
}

// Con un Error se manda la excepción para conservar el stacktrace; el nivel va explícito porque `captureException`
// sin él lo deja en `error`, y el mensaje va en `extra.logMessage` (Sentry titula el issue con la excepción) sin
// pisar un `extra.message` de quien llama. Lo que no es un Error va como mensaje: así el issue conserva el título
// fijo y no se parte en uno por cada valor rechazado.
function sendEvent(level: LogLevel, message: string, { error, extra, tags }: LogContext = {}): void {
  const sentryLevel = SENTRY_LEVEL[level];
  if (error instanceof Error) {
    Sentry.captureException(error, { tags, level: sentryLevel, extra: { ...extra, logMessage: message } });
  } else if (hasError(error)) {
    Sentry.captureMessage(message, { tags, level: sentryLevel, extra: { ...extra, ...describeError(error) } });
  } else {
    Sentry.captureMessage(message, { level: sentryLevel, tags, extra });
  }
}

function createMethod(level: LogLevel): LogMethod {
  const isEvent = level === 'warn' || level === 'error';
  return (message, context) => {
    writeToConsole(level, message, context);
    try {
      if (isEvent) {
        sendEvent(level, message, context);
      } else {
        leaveBreadcrumb(level, message, context);
      }
    } catch (sentryError) {
      globalThis.console.error('Error al enviar el log a Sentry:', sentryError);
    }
  };
}

/**
 * Único punto para registrar algo en toda la app, también desde `core/chatbot`. Cuándo usar cada nivel:
 * `docs/sentry/niveles.md`.
 */
export const logger: Record<LogLevel, LogMethod> = {
  trace: createMethod('trace'),
  debug: createMethod('debug'),
  info: createMethod('info'),
  warn: createMethod('warn'),
  error: createMethod('error'),
};
